import { ensureModeLayout, ensureShell, patchHTML } from "../core/surface-host.js";
import { patchThreadSurfaceHTML } from "../core/thread-surface-state.js";
import { renderSkeleton } from "../render/ui-helpers.js";
import { renderAgentsRoute } from "../admin-pages/agents.js";
import { renderModelsRoute } from "../admin-pages/models.js";
import { renderThreadsRoute } from "../admin-pages/threads.js";
import { renderTurnsRoute } from "../admin-pages/turns.js";
import { renderWorkspacesRoute } from "../admin-pages/workspaces.js";

export function renderAdminShell(root, state, options = {}) {
  if (!state.apiKey) {
    renderAuthOnly(root, options);
    return;
  }
  ensureAdminShell(root);
  const navRoot = root.querySelector("[data-admin-nav-root]");
  const mainRoot = root.querySelector("[data-admin-main]");
  const breadcrumbRoot = root.querySelector("[data-admin-breadcrumb-root]");
  const layoutRoot = root.querySelector("[data-admin-layout-root]");
  const drawerRoot = root.querySelector("[data-admin-drawer-root]");
  const settingsRoot = root.querySelector("[data-admin-settings-root]");
  const isThreadDetail = state.route.id === "thread-detail";
  const shellEl = root.querySelector("[data-admin-shell]");
  if (shellEl instanceof Element) {
    shellEl.classList.toggle("shell--nav-collapsed", Boolean(state.navCollapsed));
  }
  patchHTML(navRoot, options.renderNavRail?.() || "");
  patchHTML(breadcrumbRoot, options.renderBreadcrumbBar?.() || "");
  if (mainRoot instanceof Element) {
    mainRoot.className = `main ${isThreadDetail && state.apiKey && state.threadDetail.thread ? "thread-detail-layout" : ""}`.trim();
  }

  if (isThreadDetail) {
    renderAdminThreadDetailLayout(layoutRoot, state, options);
  } else if (state.route.id === "agents") {
    renderAgentsRoute(layoutRoot, state, {
      renderLockedState: options.renderLockedState,
      lookupByID: options.lookupByID,
      harnessesForAdapter: options.harnessesForAdapter,
      lookupHarnessForDraft: options.lookupHarnessForDraft,
      staleAttachedServerURL: options.staleAttachedServerURL,
      harnessDisplayLabel: options.harnessDisplayLabel,
      formatHarnessNote: options.formatHarnessNote
    });
  } else if (state.route.id === "workspaces") {
    renderWorkspacesRoute(layoutRoot, state, {
      renderLockedState: options.renderLockedState,
      lookupByID: options.lookupByID,
      truncatePath: options.truncatePath,
      workspacePrimaryAction: options.workspacePrimaryAction,
      workspacePrimaryLocation: options.workspacePrimaryLocation,
      workspaceStateSummary: options.workspaceStateSummary,
      renderWorkspacePushStatus: options.renderWorkspacePushStatus,
      renderWorkspaceCommitHistory: options.renderWorkspaceCommitHistory,
      renderWorkspaceReviewForm: options.renderWorkspaceReviewForm,
      renderWorkspacePushPanel: options.renderWorkspacePushPanel,
      renderWorkspaceContentPanelForList: options.renderWorkspaceContentPanelForList
    });
  } else if (state.route.id === "models") {
    renderModelsRoute(layoutRoot, state, {
      renderLockedState: options.renderLockedState,
      lookupName: options.lookupName,
      slugify: options.slugify
    });
  } else if (state.route.id === "threads") {
    renderThreadsRoute(layoutRoot, state, {
      renderLockedState: options.renderLockedState,
      threadTitle: options.threadTitle,
      threadPreviewText: options.threadPreviewText
    });
  } else if (state.route.id === "turns") {
    renderTurnsRoute(layoutRoot, state, {
      renderLockedState: options.renderLockedState,
      renderPendingDecisionGlance: options.renderPendingDecisionGlance,
      renderPromotionDiagnosticCard: options.renderPromotionDiagnosticCard,
      renderTurnForm: options.renderTurnForm,
      lookupName: options.lookupName
    });
  } else {
    renderAdminPageLayout(layoutRoot, state, options);
  }

  patchHTML(drawerRoot, state.detailDrawerOpen ? options.renderDetailDrawer?.() || "" : "");
  patchHTML(settingsRoot, state.settingsOpen ? options.renderSettingsPopover?.() || "" : "");
}

function renderAuthOnly(root, options = {}) {
  ensureShell(root, "[data-admin-auth-root]", `
    <main class="auth-gate" data-admin-auth-root></main>
  `);
  const authRoot = root.querySelector("[data-admin-auth-root]");
  patchHTML(authRoot, options.renderAuthDialog?.() || options.renderLockedState?.() || "");
}

function ensureAdminShell(root) {
  ensureShell(root, "[data-admin-layout-root]", `
    <div class="shell" data-admin-shell>
      <div data-admin-nav-root></div>
      <div class="main" data-admin-main>
        <div data-admin-breadcrumb-root></div>
        <div data-admin-layout-root></div>
      </div>
    </div>
    <div data-admin-drawer-root></div>
    <div data-admin-settings-root></div>
  `);
}

function renderAdminPageLayout(layoutRoot, state, options = {}) {
  ensureModeLayout(layoutRoot, "data-admin-layout-mode", "page", `<main class="content" data-admin-page-root></main>`);
  const pageRoot = layoutRoot.querySelector("[data-admin-page-root]");
  if (!state.apiKey) {
    patchHTML(pageRoot, options.renderLockedState?.() || "");
    return;
  }
  patchHTML(pageRoot, state.loading ? renderSkeleton(4) : options.renderRoutePage?.(state.route.id) || "");
}

function renderAdminThreadDetailLayout(layoutRoot, state, options = {}) {
  ensureModeLayout(layoutRoot, "data-admin-layout-mode", "thread-detail", `
    <div class="thread-detail-body" data-thread-detail-body>
      <main class="content" data-thread-detail-content-root></main>
      <div data-thread-detail-panel-root></div>
    </div>
  `);
  const bodyRoot = layoutRoot.querySelector("[data-thread-detail-body]");
  const contentRoot = layoutRoot.querySelector("[data-thread-detail-content-root]");
  const panelRoot = layoutRoot.querySelector("[data-thread-detail-panel-root]");
  if (bodyRoot instanceof Element) {
    bodyRoot.className = `thread-detail-body${state.threadContentPanelOpen ? " thread-detail-body--split" : ""}`;
  }
  const contentHTML = state.loading
    ? renderSkeleton(4)
    : (!state.apiKey ? options.renderLockedState?.() || "" : options.renderThreadDetailPage?.() || "");
  patchThreadSurfaceHTML(contentRoot, contentHTML, {
    activeElement: contentRoot instanceof Element ? contentRoot.ownerDocument?.activeElement : null
  });
  patchHTML(panelRoot, state.threadContentPanelOpen ? options.renderThreadContentPanel?.() || "" : "");
}
